import { Tag } from "@design";
import {
  BookOpen,
  FileSpreadsheet,
  FileText,
  Globe,
  HardDrive,
  type LucideIcon,
  MessagesSquare,
  Network,
  Paperclip,
  Presentation,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type Citation, CitationList } from "@/components/chat/CitationList";
import { wikiPagePath } from "@/features/knowledge/KnowledgeTabs";
import { formatDate } from "@/i18n/format";
import { type ChatSource, type NumberedSources, type SourceKind, sourceKind } from "./messageSources";

/** Chips shown before "+N more"; a research answer can search dozens of pages. */
export const VISIBLE_SOURCE_TAGS = 5;

/** An earlier project chat the assistant read or searched is a "conversation" (MHS-709). */
const SOURCE_ICONS: Record<SourceKind, LucideIcon> = {
  rag: BookOpen,
  web: Globe,
  sharepoint: FileText,
  kg: Network,
  conversation: MessagesSquare,
  attachment: Paperclip,
  google_drive: HardDrive,
  google_docs: FileText,
  google_sheets: FileSpreadsheet,
  google_slides: Presentation,
};

function sourceIcon(type: string) {
  const Icon = SOURCE_ICONS[sourceKind(type)];
  return <Icon size={12} />;
}

const sourceKey = (s: ChatSource) => `${s.type}|${s.wiki?.nodeUuid ?? s.url ?? s.name}`;

/** The site a web source came from ("arxiv.org"), for telling similar titles apart. */
export function siteOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

/** When a web page was published ("Oct 20, 2026"); undefined when there's no date or it can't be read. */
export function formatPublishedDate(value: string | undefined): string | undefined {
  const date = value ? new Date(value) : undefined;
  if (!date || Number.isNaN(date.getTime())) return undefined;
  // In UTC: a date-only value is midnight UTC, which local time west of UTC shows as the day before.
  return formatDate(date, { dateStyle: "medium", timeZone: "UTC" });
}

/** Shown beside a source's name: a wiki page's space, or the site a web page came from. */
export function sourceSection(source: ChatSource): string | undefined {
  if (source.wiki) return source.wiki.spaceName;
  return source.type === "rag" ? undefined : siteOf(source.url);
}

/** One chip per distinct source name: the same paper often comes back from several URLs. */
function uniqueByName(sources: ChatSource[]): ChatSource[] {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = source.wiki ? sourceKey(source) : `${sourceKind(source.type)}|${source.name.trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** The monospace source chips above an answer ("Knowledge · Sales Playbook · 6 passages"), first few then "+N more". */
export function SourceTags({ sources }: Readonly<{ sources: ChatSource[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "sources" });
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const unique = uniqueByName(sources);
  if (unique.length === 0) return null;
  const hidden = unique.length - VISIBLE_SOURCE_TAGS;
  const shown = expanded || hidden <= 0 ? unique : unique.slice(0, VISIBLE_SOURCE_TAGS);
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((source) => {
        const kind = t(`kinds.${sourceKind(source.type)}`);
        const label = source.chunks
          ? t("tagWithPassages", { kind, name: source.name, count: source.chunks })
          : t("tag", { kind, name: source.name });
        return (
          <Tag
            key={sourceKey(source)}
            icon={sourceIcon(source.type)}
            href={source.url}
            onClick={
              source.wiki
                ? () => source.wiki && navigate(wikiPagePath(source.wiki.spaceUuid, source.wiki.path))
                : undefined
            }
            truncate
          >
            {label}
          </Tag>
        );
      })}
      {hidden > 0 && (
        <Tag onClick={() => setExpanded((open) => !open)}>{expanded ? t("fewer") : t("more", { count: hidden })}</Tag>
      )}
    </div>
  );
}

/**
 * What a reply's "Cited N sources" trigger opens (Figma "Action row"): the numbered list of everything the tools looked
 * at, numbered as stored so the numbers match the answer's `[n]` citations. A research report splits it into the
 * sources it cites and the ones it only consulted, and its label counts the cited ones (the run's own summary already
 * says how many it found). None when the reply used no sources.
 */
export function useCitedSources(sources: NumberedSources): { label: string; content: ReactNode } | null {
  const { t } = useTranslation("chat", { keyPrefix: "sources" });
  const navigate = useNavigate();
  const rows = sources.flatMap((source, index) => (source ? [{ source, number: index + 1 }] : []));
  if (rows.length === 0) return null;
  const toCitation = ({ source, number }: { source: ChatSource; number: number }): Citation => {
    const wiki = source.wiki;
    const published = formatPublishedDate(source.publishedDate);
    return {
      key: String(number),
      number,
      source: source.name,
      section: sourceSection(source),
      snippet: source.snippet,
      updated: published ? t("published", { date: published }) : undefined,
      ...(wiki
        ? { onOpen: () => navigate(wikiPagePath(wiki.spaceUuid, wiki.path)), openLabel: t("openPage") }
        : {
            onOpen: source.url ? () => window.open(source.url, "_blank", "noopener,noreferrer") : undefined,
            openLabel: source.type === "rag" ? undefined : t("open"),
          }),
    };
  };
  const cited = rows.filter((row) => row.source.cited === true);
  const consulted = rows.filter((row) => row.source.cited !== true);
  const label = cited.length === 0 ? t("searched", { count: rows.length }) : t("citedCount", { count: cited.length });
  return {
    label,
    content:
      cited.length === 0 ? (
        <CitationList citations={rows.map(toCitation)} />
      ) : (
        <>
          <CitationList heading={t("cited")} citations={cited.map(toCitation)} />
          {consulted.length > 0 && (
            <CitationList heading={t("alsoConsulted")} citations={consulted.map(toCitation)} numbered={false} />
          )}
        </>
      ),
  };
}
